/**
 * Canned / saved replies client for Personal settings (mine list, update, delete).
 * Live inbox: human-agent-service /api/v1/agent/canned-responses. Otherwise skeleton store.
 * Workspace scope: X-Workspace-Id via inboxWorkspaceHeaders (SECURITY.md §2.6).
 */
import { getBearerAuthHeaders } from "@/lib/authSession";
import { fetchWithAuthRetry } from "@/lib/authedRequest";
import { isHumanAgentInboxLive, inboxWorkspaceHeaders } from "@/lib/inboxLiveConfig";
import { cannedResponsesSkeletonStore } from "@/api/cannedResponsesSkeletonStore";

const stubDelay = (ms = 80) => new Promise((r) => setTimeout(r, ms));

export class CannedResponsesClient {
  /**
   * @param {{ live?: boolean, store?: import("./cannedResponsesSkeletonStore").CannedResponsesSkeletonStore }} [opts]
   */
  constructor({ live = isHumanAgentInboxLive(), store = cannedResponsesSkeletonStore } = {}) {
    this._live = live;
    this._store = store;
  }

  /**
   * @param {{ workspaceId?: string | null }} [params]
   * @returns {Promise<{ data: Array<Record<string, unknown>> }>}
   */
  async listMine({ workspaceId } = {}) {
    if (!this._live) {
      await stubDelay();
      return { data: this._store.listMine() };
    }
    const json = await this._request("/api/v1/agent/canned-responses", {
      workspaceId,
      searchParams: { mine: "true" },
    });
    return { data: Array.isArray(json?.data) ? json.data : [] };
  }

  /**
   * @param {{ id: string, workspaceId?: string | null, shortcut?: string, title?: string, body?: string, visibility?: "public" | "pvt" | "workspace_wide" }} payload
   */
  async update(payload) {
    if (!this._live) {
      await stubDelay();
      return { data: this._store.update(payload) };
    }
    const body = {};
    if (payload.shortcut !== undefined) body.shortcut = payload.shortcut;
    if (payload.title !== undefined) body.title = payload.title;
    if (payload.body !== undefined) body.body = payload.body;
    if (payload.visibility !== undefined) body.visibility = payload.visibility;
    const json = await this._request(`/api/v1/agent/canned-responses/${encodeURIComponent(payload.id)}`, {
      method: "PATCH",
      workspaceId: payload.workspaceId,
      body,
    });
    return { data: json?.data ?? json };
  }

  /**
   * @param {{ id: string, workspaceId?: string | null }} params
   */
  async delete({ id, workspaceId }) {
    if (!this._live) {
      await stubDelay();
      this._store.delete(id);
      return;
    }
    await this._request(`/api/v1/agent/canned-responses/${encodeURIComponent(id)}`, {
      method: "DELETE",
      workspaceId,
    });
  }

  async _request(path, { method = "GET", body, workspaceId, searchParams } = {}) {
    const buildRequest = () => {
      const url = new URL(path, import.meta.env?.VITE_API_BASE_URL || window.location.origin);
      if (searchParams && typeof searchParams === "object") {
        for (const [key, value] of Object.entries(searchParams)) {
          if (value === undefined || value === null || value === "") continue;
          url.searchParams.set(key, String(value));
        }
      }
      return fetch(url.toString(), {
        method,
        credentials: "include",
        headers: {
          Accept: "application/json",
          ...getBearerAuthHeaders(),
          ...inboxWorkspaceHeaders(workspaceId),
          ...(body !== undefined ? { "Content-Type": "application/json" } : {}),
        },
        ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
      });
    };
    const res = await fetchWithAuthRetry(buildRequest);
    if (!res.ok) {
      let detail = res.statusText;
      try {
        const json = await res.json();
        if (json?.detail) {
          detail = typeof json.detail === "string" ? json.detail : json.detail?.message || JSON.stringify(json.detail);
        }
        if (json?.error) detail = typeof json.error === "string" ? json.error : JSON.stringify(json.error);
      } catch {
        /* ignore */
      }
      const err = new Error(detail || `Request failed (${res.status})`);
      err.status = res.status;
      throw err;
    }
    if (res.status === 204) return null;
    return res.json();
  }
}
